import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Button, Loading, Radio } from '@linktivity/link-ui';

import { useStore } from '@link/shinkansen/stores';
import { SeatPreferenceCode } from '@link/shinkansen/services/types';

import styles from './seatSelection.module.css';

const SeatSelectionView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shinkansen } = useStore();
  const { bookingInfo, orderParameters, selection } = shinkansen;
  const seatPreferences = useMemo(
    () => orderParameters?.seatPreferences ?? [],
    [orderParameters]
  );

  useEffect(() => {
    if (!bookingInfo) {
      void shinkansen.getBookingData(shinkansen.bookingId || undefined);
    }
  }, [bookingInfo, shinkansen]);

  useEffect(() => {
    if (!orderParameters) {
      void shinkansen.getOrderParameters();
    }
  }, [orderParameters, shinkansen]);

  useEffect(() => {
    if (!selection.seatPreferenceCode && seatPreferences[0]) {
      shinkansen.setSeatPreference(seatPreferences[0]);
    }
  }, [seatPreferences, selection.seatPreferenceCode, shinkansen]);

  if (!bookingInfo || !orderParameters) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>{t('views.seat.title')}</h1>
        {!!bookingInfo.units?.length && (
          <p className={styles.ticketCount}>
            {t('views.trainList.ticketCount')}
            {bookingInfo.units
              .map(unit => `${unit.name} ×${unit.quantity ?? 0}`)
              .join(' ')}
          </p>
        )}

        <h2 className={styles.sectionTitle}>{t('views.seat.preference')}</h2>
        <Radio.Group
          className={styles.options}
          value={selection.seatPreferenceCode}
          onChange={value => {
            const preference = seatPreferences.find(
              item => item.code === value
            );
            if (preference) {
              shinkansen.setSeatPreference(preference);
              return;
            }
            shinkansen.setSeatPreferenceCode(value as SeatPreferenceCode);
          }}
        >
          {seatPreferences.map(preference => (
            <Radio
              key={preference.code}
              className={styles.option}
              value={preference.code}
              label={preference.name}
            />
          ))}
        </Radio.Group>
      </div>

      <div className={styles.footer}>
        <Button
          variant="filled"
          className={styles.nextButton}
          onClick={() => navigate('../confirm')}
        >
          {t('views.seat.next')}
        </Button>
      </div>
    </div>
  );
};

const SeatSelection = observer(SeatSelectionView);

export default SeatSelection;
